import {
  Directive,
  ViewChild,
  HostListener,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  effect,
  inject,
} from '@angular/core';
import { DxSchedulerComponent } from 'devextreme-angular';
import { OptionChangedEvent, Properties as DxSchedulerProperties } from 'devextreme/ui/scheduler';
import Toolbar from 'devextreme/ui/toolbar';
import { firstValueFrom, interval, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { DriverDto, DriverNoteType, LicenseType } from '../shared/generated/backend';
import { TranslateService } from '@ngx-translate/core';
import { ItemClickEvent } from 'devextreme/ui/drop_down_button';
import { SchedulerProjectInfoDto } from '../shared/generated/backend/model/schedulerProjectInfoDto';
import { DateRangeStore } from '../services/date-range.service';
import { DateHelper } from '../shared/util/date-helper';
import { addDays } from 'date-fns';
import { AutoSyncService } from '../services/auto-sync.service';

interface ScrollPosition {
  verticalElement?: Element;
  horizontalOffset?: number;
}
export enum AppointmentType {
  NOTE = 'NOTE',
  PROJECT = 'PROJECT',
  LONG_WEEK = 'LONG_WEEK',
  SHORT_WEEK = 'SHORT_WEEK',
}

export enum DriverNotificationState {
  DRIVER_INFORMED = 'DRIVER_INFORMED',
  NOTIFICATION_RECEIVED = 'NOTIFICATION_RECEIVED',
  TOUR_CONFIRMED = 'TOUR_CONFIRMED',
}

export enum NoteType {
  TRANSPORT = 'TRANSPORT',
  SICK = 'SICK',
  VACATION = 'VACATION',
  PRIVATE = 'PRIVATE',
  SCHOOL = 'SCHOOL',
  FREE = 'FREE',
  CANCELLED = 'CANCELLED',
}

export interface Driver extends DriverDto {
  id: number;
  text: string;
}

export interface Appointment extends SchedulerProjectInfoDto {
  startDate: Date | undefined;
  endDate: Date | undefined;
  Text: string;
  NoteType: DriverNoteType | null;
  AppointmentType: AppointmentType;
  drivingLicense?: LicenseType;
  disabled?: boolean;
  checkNeeded?: boolean;
}

// Add interfaces for type safety
export interface ContentReadyEvent {
  element: HTMLElement;
}

export interface ToolbarItem {
  defaultElement?: string;
  widget?: string;
  location?: string;
  locationInMenu?: string;
  name?: string;
  options?: Record<string, unknown>;
}

export interface AutoPollingEvent {
  value: boolean;
}

export type SchedulerViews = NonNullable<DxSchedulerProperties['views']>;

@Directive()
export abstract class BaseSchedulerComponent implements OnInit, OnDestroy {
  private readonly autoSyncService = inject(AutoSyncService);
  readonly isAutoPollingEnabled = this.autoSyncService.isAutoPollingEnabled;

  // Common properties
  protected isLoading = false;
  protected blockAutoSync = false;
  protected schedulerHeight: number = 800;
  protected fromDate!: Date;
  protected toDate!: Date;
  private dateRange!: [Date, Date];

  protected isComponentActive = true;
  protected pollingSubscription?: Subscription;
  protected readonly POLLING_INTERVAL = 30000; // 30 seconds
  protected appointments: Appointment[] = [];
  protected drivers: Driver[] = [];

  // Common view configurations
  protected currentViewName = '';
  protected views: SchedulerViews = [
    {
      type: 'timelineWeek',
      name: '6h',
      cellDuration: 360,
      groupOrientation: 'vertical',
      startDayHour: 0,
      endDayHour: 24,
    },
  ];
  @ViewChild(DxSchedulerComponent, { static: false }) scheduler!: DxSchedulerComponent;

  get viewName() {
    return this.currentViewName;
  }

  private isSchedulerViewObject(
    view: (typeof this.views)[number]
  ): view is Exclude<(typeof this.views)[number], string> {
    return typeof view !== 'string';
  }

  protected translationeService = inject(TranslateService);
  protected cdr = inject(ChangeDetectorRef);
  protected dateRangeStore = inject(DateRangeStore);

  constructor() {
    this.setSchedulerHeight();
    this.dateRange = this.dateRangeStore.range();
    this.fromDate = this.dateRange[0];
    this.toDate = this.dateRange[1];
    effect(() => {
      this.dateRange = this.dateRangeStore.range();
      this.fromDate = this.dateRange[0];
      this.toDate = this.dateRange[1];
    });
  }

  onContentReady() {
    this.prepareToolbar();
  }

  async ngOnInit(): Promise<void> {
    await this.waitForTranslations();
    this.loadInitialViews();
    this.setSchedulerHeight();
    await this.loadInitialData();
    if (this.isAutoPollingEnabled()) {
      this.startPolling();
    }
    this.updateScheduleIntervalCount(this.viewName);
  }

  async waitForTranslations() {
    await firstValueFrom(this.translationeService.get('HEADER.TRANSPORT_MODULE'));
  }

  loadInitialViews() {
    const intervalCount = this.calcDaysCount() + 1;

    this.views = [
      {
        type: 'timelineDay',
        name: this.translationeService.instant('DATETIME.6_HOURS'),
        cellDuration: 360,
        groupOrientation: 'vertical',
        startDayHour: 0,
        endDayHour: 24,
        intervalCount,
      },
      {
        type: 'timelineDay',
        name: this.translationeService.instant('DATETIME.4_HOURS'),
        cellDuration: 240,
        groupOrientation: 'vertical',
        startDayHour: 0,
        endDayHour: 24,
        intervalCount,
      },
      {
        type: 'timelineDay',
        name: this.translationeService.instant('DATETIME.3_5_HOURS'),
        cellDuration: 210,
        groupOrientation: 'vertical',
        startDayHour: 0,
        endDayHour: 21,
        intervalCount,
      },
      {
        type: 'timelineDay',
        name: this.translationeService.instant('DATETIME.3_HOURS'),
        cellDuration: 180,
        groupOrientation: 'vertical',
        startDayHour: 0,
        endDayHour: 24,
        intervalCount,
      },
      {
        type: 'timelineDay',
        name: this.translationeService.instant('DATETIME.2_5_HOURS'),
        cellDuration: 150,
        groupOrientation: 'vertical',
        startDayHour: 0,
        endDayHour: 20,
        intervalCount,
      },
      {
        type: 'timelineDay',
        name: this.translationeService.instant('DATETIME.2_HOURS'),
        cellDuration: 120,
        groupOrientation: 'vertical',
        startDayHour: 0,
        endDayHour: 24,
        intervalCount,
      },
      {
        type: 'timelineDay',
        name: this.translationeService.instant('DATETIME.1_5_HOURS'),
        cellDuration: 90,
        groupOrientation: 'vertical',
        startDayHour: 0,
        endDayHour: 24,
        intervalCount,
      },
      {
        type: 'timelineDay',
        name: this.translationeService.instant('DATETIME.1_HOURS'),
        cellDuration: 60,
        groupOrientation: 'vertical',
        startDayHour: 0,
        endDayHour: 24,
        intervalCount,
      },
      {
        type: 'timelineDay',
        name: this.translationeService.instant('DATETIME.30_MINUTES'),
        cellDuration: 30,
        groupOrientation: 'vertical',
        startDayHour: 0,
        endDayHour: 24,
        intervalCount,
      },
      {
        type: 'timelineDay',
        name: this.translationeService.instant('DATETIME.15_MINUTES'),
        cellDuration: 15,
        groupOrientation: 'vertical',
        startDayHour: 0,
        endDayHour: 24,
        intervalCount,
      },
    ];
    const firstView = this.views[0];
    this.currentViewName = firstView && this.isSchedulerViewObject(firstView) ? (firstView.name ?? '') : '';
  }

  @HostListener('window:resize')
  protected onResize(): void {
    this.setSchedulerHeight();
    this.repaintScheduler();
  }

  protected setSchedulerHeight(): void {
    this.schedulerHeight = window.innerHeight - 80;
  }

  isRepainting: boolean = false;
  repaintingRequested: boolean = false;
  protected repaintScheduler(): void {
    this.scheduler?.instance.repaint();
  }

  // Abstract methods that must be implemented by child classes
  protected abstract loadInitialData(): Promise<void>;
  protected abstract loadAppointments(): Promise<void>;

  // Common toolbar handling
  protected prepareToolbar(): void {
    const toolbarEl = document.querySelector('.dx-toolbar');
    if (!toolbarEl) return;
    const toolbar = Toolbar.getInstance(toolbarEl)!;
    const items = toolbar.option('items') as ToolbarItem[];

    // 1) Replace the old dxButtonGroup with a date‐range picker
    const dpIdx = items.findIndex(i => i.widget === 'dxButtonGroup');
    if (dpIdx >= 0) {
      const baseOpts = items[dpIdx].options;
      let clearingTo = false;

      let committedInThisOpen = false;
      let pendingFromChange = false;

      items[dpIdx] = {
        widget: 'dxDateRangeBox',
        name: 'dateRange',
        location: 'before',
        locationInMenu: 'always',
        options: {
          ...baseOpts,
          width: 240,
          labelMode: 'hidden',
          showLabels: false,
          value: this.dateRange,
          applyValueMode: 'instantly',
          onOpened: () => {
            this.toolboxWidgetOpened();
            committedInThisOpen = false;
            pendingFromChange = false;
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClosed: (e: any) => {
            this.toolboxWidgetClosed();
            if (!committedInThisOpen) {
              const [from, to] = e.component.option('value') as [Date | null, Date | null];

              const differsFromStore =
                !this.dateRange ||
                !DateHelper.isSame(from, this.dateRange[0]) ||
                !DateHelper.isSame(to, this.dateRange[1]);

              if (from && to && (pendingFromChange || differsFromStore)) {
                this.dateRange = [from, to];
                this.dateRangeStore.range.set([from, to]);
                this.loadAppointmentsWhenChanged();
                committedInThisOpen = true;
                pendingFromChange = false;
              }
            }
          },
          onValueChanged: (args: {
            value: [Date | null, Date | null];
            previousValue: [Date | null, Date | null];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            component: any;
          }) => {
            const [newFrom, newTo] = args.value;
            /* eslint-disable @typescript-eslint/no-unused-vars */
            const [oldFrom, oldTo] = args.previousValue;

            // --- Step A: user just changed FROM?
            if (newFrom && (!oldFrom || newFrom.getTime() !== oldFrom.getTime()) && !clearingTo) {
              pendingFromChange = true; // remember we changed FROM
              clearingTo = true;
              setTimeout(() => {
                // If new FROM is after current TO -> clear TO to force picking a new end date
                const shouldClearTo = !!oldTo && newFrom.getTime() > oldTo.getTime();
                args.component.option('value', [newFrom, shouldClearTo ? null : oldTo]);
                clearingTo = false;
              }, 0);
              return; // don't commit yet
            }

            // --- Step B: both from & to are set → commit them now
            if (newFrom && newTo) {
              // 1) push into your helper/store
              this.dateRange = [newFrom, newTo];
              this.dateRangeStore.range.set([newFrom, newTo]);

              // 2) reload
              this.loadAppointmentsWhenChanged();

              committedInThisOpen = true;
              pendingFromChange = false;
            }
          },
        },
      };
    }

    const vsIndex = items.findIndex(i => i.defaultElement === 'viewSwitcher');
    if (vsIndex >= 0) {
      items[vsIndex].location = 'before';
      items[vsIndex].name = 'viewSwitcher';

      const options = (items[vsIndex].options ?? {}) as Record<string, unknown> & {
        onItemClick?: (e: ItemClickEvent) => void | Promise<void>;
        __wrapped?: boolean;
      };

      if (!options.__wrapped && options.onItemClick) {
        const oldClick = options.onItemClick;

        options.onItemClick = async (e: ItemClickEvent) => {
          this.isLoading = true;
          this.viewSwitcherClicked(e);
          await new Promise(r => setTimeout(r, 200));
          await oldClick(e);
          this.isLoading = false;
        };

        options.__wrapped = true;
        items[vsIndex].options = options;
      }
    }

    // 3) push in refresh + autoPolling if missing
    if (!items.some(i => i.name === 'refreshButton')) {
      items.push({
        widget: 'dxButton',
        name: 'refreshButton',
        location: 'before',
        locationInMenu: 'always',
        options: {
          icon: 'refresh',
          onClick: () => this.loadAppointments(),
        },
      });
      items.push({
        widget: 'dxCheckBox',
        name: 'autoPolling',
        location: 'before',
        locationInMenu: 'always',
        options: {
          text: 'Auto-sync',
          value: this.isAutoPollingEnabled(),
          onValueChanged: (e: unknown) => this.onAutoPollingChanged(e as AutoPollingEvent),
        },
      });
    }

    toolbar.option('items', items);
  }

  toolboxWidgetOpened() {
    this.blockAutoSync = true;
  }

  toolboxWidgetClosed() {
    this.blockAutoSync = false;
  }

  loadAppointmentsWhenChanged() {
    this.fromDate = this.dateRange[0];
    this.toDate = this.dateRange[1];

    const view = (this.scheduler?.instance.option('currentView') as string) || this.currentViewName;
    if (view) {
      this.currentViewName = view;
      this.updateScheduleIntervalCount(view);
    }

    this.loadAppointments();
  }

  viewSwitcherClicked(e: ItemClickEvent) {
    e.event?.preventDefault();
    this.isLoading = true;

    const view = e.itemData?.name;
    if (view) {
      this.currentViewName = view;
      this.updateScheduleIntervalCount(view);
    }

    this.repaintScheduler();
    this.isLoading = false;
  }

  protected getWeekColor(appointment: Appointment): string {
    return appointment.AppointmentType === AppointmentType.LONG_WEEK ? '#14f5f5' : '#00BABAFE';
  }

  // Common polling functionality
  protected startPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }

    if (this.isAutoPollingEnabled()) {
      this.pollingSubscription = interval(this.POLLING_INTERVAL)
        .pipe(takeWhile(() => this.isComponentActive && this.isAutoPollingEnabled()))
        .subscribe({
          next: () => {
            if (!this.isLoading && !this.blockAutoSync) this.loadAppointments();
          },
          error: () => {
            console.error('Error while polling for appointments');
            this.startPolling();
          },
        });
    }
  }

  protected onAutoPollingChanged(e: AutoPollingEvent): void {
    this.autoSyncService.toggleEnabled();
    if (this.isAutoPollingEnabled()) {
      this.startPolling();
    } else {
      if (this.pollingSubscription) {
        this.pollingSubscription.unsubscribe();
      }
    }
  }

  dateRangeChange(e: OptionChangedEvent) {
    const [from, to] = e.value as [Date, Date];
    if (from == null || to == null) {
      return;
    }
    this.dateRangeStore.range.set([from, to]);
  }

  // Common date handling
  protected getUpdatedDateRange(): { fromDate: Date; toDate: Date; dateRange: [Date, Date]; daysCount: number } {
    const daysCount = this.calcDaysCount();
    this.toDate = addDays(this.fromDate, daysCount);
    this.dateRange = [this.fromDate, this.toDate];
    this.cdr.detectChanges();
    return { fromDate: this.fromDate, toDate: this.toDate, dateRange: this.dateRange, daysCount: daysCount };
  }

  private calcDaysCount = (currentView?: string) => {
    // get the days between the from and to date
    if (!this.fromDate || !this.toDate) {
      return 7;
    }
    // currentView ??= this.scheduler?.instance.option('currentView');

    const diff = Math.abs(this.toDate.getTime() - this.fromDate.getTime());
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  // Returns the number of cells in each day depending on the current view
  calcNumberOfCellsPerDay = (): number => {
    switch (this.scheduler?.instance.option('currentView')) {
      case '6h':
        return 4;

      case '4h':
      case '3.5h':
        return 6;

      case '3h':
      case '2.5h':
        return 8;

      case '2h':
        return 12;
      case '1.5h':
        return 16;
      case '1h':
        return 24;
      case '30m':
        return 48;
      case '15m':
        return 96;
      default:
        return 0;
    }
  };

  // Returns the number of hours per cell depending on the current view
  calcCellOffsetToHours = (): number => {
    switch (this.scheduler?.instance.option('currentView')) {
      case '6h':
        return 6;
      case '4h':
        return 4;
      case '3.5h':
        return 3.5;
      case '3h':
        return 3;
      case '2.5h':
        return 2.5;
      case '2h':
        return 2;
      case '1.5h':
        return 1.5;
      case '1h':
        return 1;
      case '30m':
        return 0.5;
      case '15m':
        return 0.25;
      default:
        return 0;
    }
  };

  // Converts the horizontal scrollPosition into a date
  scrollPositionToDate = (scrollPosition: ScrollPosition): Date => {
    // e.g. we're at scrollPosition.horizontalOffset 151 (out of 192) with currentView = 2h
    // (this means each day has 12 columns with 2h each)
    // horizontalOffsetDay = floor(151 / 12) = 12 => we're on the 13th day
    // horizontalOffsetHours = (151 % 12) * 2 = 14 => we're at 14:00
    // => the resulting date is start Date + 12 days at 14:00
    const horizontalOffsetDay = Math.floor((scrollPosition.horizontalOffset ?? 0) / this.calcNumberOfCellsPerDay());
    const horizontalOffsetHours =
      ((scrollPosition.horizontalOffset ?? 0) % this.calcNumberOfCellsPerDay()) * this.calcCellOffsetToHours();
    const date = new Date(this.fromDate);
    date.setDate(this.fromDate.getDate() + horizontalOffsetDay);
    date.setHours(Math.floor(horizontalOffsetHours), 60 * (horizontalOffsetDay % 1), 0, 0);
    // Don't remove the commented out code, it's super handy for debugging this ;)
    // console.log(`scrollPosition  ${scrollPosition.horizontalOffset ?? 0}`);
    // console.log(`horizontalOffsetDay  ${horizontalOffsetDay} (${this.calcNumberOfCellsPerDay()})`);
    // console.log(`horizontalOffsetHours  ${horizontalOffsetHours} (${this.calcCellOffsetToHours()})`);
    // console.log(`New date  ${date}`);
    return date;
  };

  private VERTICAL_SELECTOR_CONTAINER = '.dx-scheduler-work-space-flex-container';
  private HORIZONTAL_SELECTOR_CONTAINER = '.dx-scheduler-header-tables-container';
  // Checks if the given element is inside the boundingRect
  isInBoundingRect(element: Element, boundingRect: DOMRect, direction: 'horizontal' | 'vertical'): boolean {
    const rect = element.getBoundingClientRect();
    if (direction === 'horizontal') {
      return rect.left >= boundingRect.left && rect.right <= boundingRect.right;
    } else {
      return rect.top >= boundingRect.top && rect.bottom <= boundingRect.bottom;
    }
  }

  // Gets the scrollPosition - that is the central horizontal and vertical elements
  getScrollPosition(verticalSelector: string): ScrollPosition {
    // Selects all the vertical elements, e.g. vehicles or drivers
    const verticalElements = document.querySelectorAll(verticalSelector);
    const verticalMiddle = this.getMiddleVisibleElementIndex(verticalElements, 'vertical');

    // Selector for all header cells containing a time (not the day cell!)
    const horizontalElements = document.querySelectorAll(
      '.dx-scheduler-header-panel-cell.dx-scheduler-cell-sizes-horizontal:not(.dx-scheduler-header-panel-week-cell)'
    );
    const horizontalMiddle = this.getMiddleVisibleElementIndex(horizontalElements, 'horizontal');

    return {
      horizontalOffset: horizontalMiddle,
      verticalElement: verticalMiddle ? verticalElements[verticalMiddle] : undefined,
    };
  }

  getMiddleVisibleElementIndex(
    elements: NodeListOf<Element>,
    direction: 'horizontal' | 'vertical'
  ): number | undefined {
    let first = undefined;
    let last = undefined;

    // the outer container containing the elements
    const containers = document.querySelectorAll(
      direction === 'horizontal' ? this.HORIZONTAL_SELECTOR_CONTAINER : this.VERTICAL_SELECTOR_CONTAINER
    );
    if (containers.length === 0) return undefined;
    const container = containers[0];
    const boundingRect = container.getBoundingClientRect();

    // iterate over all elements and check if they are visible. At the end we'll have the first and the last visible elements
    for (let i = 0; i < elements.length; i++) {
      if (this.isInBoundingRect(elements[i], boundingRect, direction)) {
        if (first === undefined) {
          // first
          first = i;
        } else {
          // last
          last = i;
        }
      }
    }
    if (first !== undefined && last !== undefined) {
      return Math.floor((first + last) / 2);
    }
    return undefined;
  }

  // Common scheduler handling, update the interval count based on the selected date range
  updateScheduleIntervalCount = (view: string) => {
    if (!view) {
      return;
    }

    this.currentViewName = view;
    const intervalCount = this.calcDaysCount(view) + 1;

    this.views = this.views.map(v => {
      if (!this.isSchedulerViewObject(v)) {
        return v;
      }

      if (v.name === view) {
        return {
          ...v,
          intervalCount,
        };
      }

      return v;
    });

    if (this.scheduler?.instance) {
      this.isLoading = true;
      const selectedViewIndex = this.views.findIndex(v => this.isSchedulerViewObject(v) && v.name === view);
      if (selectedViewIndex >= 0) {
        this.scheduler.instance.option(`views[${selectedViewIndex}].intervalCount`, intervalCount);
        this.scheduler.instance.option('currentView', view);
      }
      this.isLoading = false;
    }

    return;
  };

  ngOnDestroy(): void {
    this.isComponentActive = false;
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  getAppointmentClass(appointmentData: Appointment): string {
    // if (this.selectedAdministrators.length > 0) {
    //   if (!this.isAdminSelected(appointmentData)) {
    //     return 'lowered-opacity';
    //   }
    // }

    return '';
  }

  isWeekend(date: Date): boolean {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday = 0, Saturday = 6
  }

  protected getToolbarInstance(): ReturnType<typeof Toolbar.getInstance> | null {
    const toolbarElement = document.querySelector('.dx-toolbar');
    return toolbarElement ? Toolbar.getInstance(toolbarElement) : null;
  }

  protected upsertToolbarItem(items: ToolbarItem[], newItem: ToolbarItem): void {
    const index = items.findIndex(i => i.name === newItem.name);

    if (index === -1) {
      items.push(newItem);
      return;
    }

    items[index] = {
      ...items[index],
      ...newItem,
      options: {
        ...(items[index].options ?? {}),
        ...(newItem.options ?? {}),
      },
    };
  }

  protected syncToolbarItemOptions(name: string, patch: Record<string, unknown>): void {
    const toolbarInstance = this.getToolbarInstance();
    if (!toolbarInstance) {
      return;
    }

    const items = [...(toolbarInstance.option('items') as ToolbarItem[])];
    const index = items.findIndex(i => i.name === name);

    if (index < 0) {
      return;
    }

    items[index] = {
      ...items[index],
      options: {
        ...(items[index].options ?? {}),
        ...patch,
      },
    };

    toolbarInstance.option('items', items);
  }
}
